// External imports
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";

// Internal imports
import { restaurantsService } from "../services/restaurants.service";
import { RestaurantFilters } from "../types/restaurants.types";

/**
 * Hook for fetching paginated saved restaurants with filters
 */
export const useRestaurants = (filters: RestaurantFilters = {}) => {
  return useInfiniteQuery({
    queryKey: ["saved-restaurants", filters],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await restaurantsService.getRestaurants({
        ...filters,
        page: pageParam,
        pageSize: filters.pageSize || 20,
      });

      if (!response.success) {
        throw new Error("Failed to fetch saved restaurants");
      }

      return response.data;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.hasNextPage ? lastPage.page + 1 : undefined;
    },
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for checking if a restaurant is saved (by Google Places ID)
 */
export const useRestaurantSaveStatus = (placeId: string) => {
  return useQuery({
    queryKey: ["saved-check", placeId],
    queryFn: async () => {
      const response = await restaurantsService.checkRestaurantSaved(placeId);

      if (!response.success) {
        throw new Error("Failed to check saved status");
      }

      return response.data;
    },
    enabled: !!placeId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 1, // Only retry once for status checks
  });
};
